"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Phase = {
  number: string;
  title: string;
  description: string;
  duration: string;
  deliverable: string;
  stakeholder: string;
  active: boolean;
};
const phases: Phase[] = [{
  number: "01",
  title: "Community Assessment",
  description: "We evaluate current water usage patterns and infrastructure health across the service area. By engaging with local committee members and reviewing historical data, we identify immediate needs and long-term maintenance priorities.",
  duration: "2 to 3 weeks",
  deliverable: "Needs assessment",
  stakeholder: "Committee members",
  active: false
}, {
  number: "02",
  title: "System Planning",
  description: "Foundational operational standards are established for water treatment and distribution. We codify safety protocols, maintenance schedules, and resource allocation plans to ensure reliable service for every household.",
  duration: "3 weeks",
  deliverable: "Operational plan",
  stakeholder: "Operators & staff",
  active: true
}, {
  number: "03",
  title: "Infrastructure Upgrades",
  description: "Essential repairs and system improvements are executed with precision. Each upgrade is documented and tested against our quality standards to ensure consistent water pressure and purity for the entire community.",
  duration: "6 to 10 weeks",
  deliverable: "System report",
  stakeholder: "Technical team",
  active: false
}, {
  number: "04",
  title: "Community Education",
  description: "Every policy, safety guideline, and service update is captured in our public resource library. We provide clear, accessible documentation so community members understand their water service and how to report issues.",
  duration: "Continuous",
  deliverable: "Resource portal",
  stakeholder: "All community members",
  active: false
}, {
  number: "05",
  title: "Ongoing Governance",
  description: "We maintain transparency through regular committee meetings and performance reporting. Our governance model is designed to scale, ensuring the nonprofit remains accountable and responsive to community needs.",
  duration: "Ongoing",
  deliverable: "Annual report",
  stakeholder: "Board & community",
  active: false
}];
function PhaseRow({
  phase,
  index
}: {
  phase: Phase;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className="border-t border-border pt-10 md:pt-14" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(12px)",
    transition: `opacity 500ms cubic-bezier(0.2, 0.7, 0.2, 1) ${index * 60}ms, transform 500ms cubic-bezier(0.2, 0.7, 0.2, 1) ${index * 60}ms`
  }}>
      <div className="grid grid-cols-8 gap-6">
        <div className="col-span-8 md:col-span-1 relative">
          {phase.active && <span aria-hidden="true" className="hidden md:block absolute -left-4 top-3 h-2 w-2 bg-primary" />}
          <div className="flex md:block items-baseline gap-4">
            <span className="font-default text-3xl md:text-5xl lg:text-6xl font-medium text-muted-foreground" style={{
            letterSpacing: "-0.02em",
            lineHeight: 1
          }}>
              {phase.number}
            </span>
          </div>
        </div>

        <div className="col-span-8 md:col-span-7">
          <h3 className="font-default text-2xl md:text-3xl lg:text-[2rem] font-medium text-foreground" style={{
          letterSpacing: "-0.01em",
          lineHeight: 1.1
        }}>
            {phase.title}
          </h3>

          <p className="mt-4 text-base text-foreground/90 max-w-[60ch]" style={{
          lineHeight: 1.6
        }}>
            {phase.description}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-[0.75rem] font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.12em"
            }}>
                Duration
              </span>
              <span className="text-[0.8125rem] text-foreground" style={{
              letterSpacing: "0.01em"
            }}>
                {phase.duration}
              </span>
            </div>

            <span aria-hidden="true" className="hidden sm:inline-block h-3 w-px bg-border" />

            <div className="flex items-baseline gap-2">
              <span className="text-[0.75rem] font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.12em"
            }}>
                Deliverable
              </span>
              <span className="text-[0.8125rem] text-foreground" style={{
              letterSpacing: "0.01em"
            }}>
                {phase.deliverable}
              </span>
            </div>

            <span aria-hidden="true" className="hidden sm:inline-block h-3 w-px bg-border" />

            <div className="flex items-baseline gap-2">
              <span className="text-[0.75rem] font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.12em"
            }}>
                Stakeholder
              </span>
              <span className="text-[0.8125rem] text-foreground" style={{
              letterSpacing: "0.01em"
            }}>
                {phase.stakeholder}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export default function Process() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="10794" id="how-it-works" className="bg-background text-foreground py-28 md:py-40 border-t border-border">
      <div className="mx-auto w-full max-w-[1440px] px-5 md:px-12 lg:px-12">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <div ref={introRef} className="md:sticky md:top-28" style={{
            opacity: introVisible ? 1 : 0,
            transform: introVisible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 700ms cubic-bezier(0.2, 0.7, 0.2, 1), transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1)"
          }}>
              <span className="text-[0.75rem] font-medium uppercase text-muted-foreground" style={{
              letterSpacing: "0.12em"
            }}>
                Our Process
              </span>

              <h2 className="font-default mt-6 text-4xl md:text-5xl lg:text-6xl font-medium text-foreground" style={{
              letterSpacing: "-0.02em",
              lineHeight: 1.0
            }}>
                The five-phase{" "}
                <span className="italic relative whitespace-nowrap">
                  approach
                  <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1 bg-primary" style={{
                  height: "4px"
                }} />
                </span>
                .
              </h2>

              <p className="mt-8 text-lg md:text-[1.1875rem] text-foreground/90 max-w-[44ch]" style={{
              lineHeight: 1.55
            }}>
                Water Service Provider Guide treats community water supply as essential infrastructure. Each engagement follows these five phases, sequenced to move from assessment to operational excellence and community transparency.
              </p>
            </div>
          </div>

          <div className="col-span-12 md:col-span-8 mt-4 md:mt-0">
            <div className="flex flex-col gap-10 md:gap-14">
              {phases.map((phase, i) => <PhaseRow key={phase.number} phase={phase} index={i} />)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}